import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileDown, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { errMsg, num, useApi } from "@/lib/api";
import { downloadGriReport } from "@/lib/griPdf";
import { Block, GriTable, KpiCards, PeriodPicker } from "@/components/gri/GriParts";
import { Narrative } from "@/components/gri/Narrative";

const year = new Date().getFullYear();

export default function GriReport() {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const [period, setPeriod] = useState({ mode: "anio", desde: `${year}-01-01`, hasta: `${year}-12-31` });
  const [rep, setRep] = useState(null);

  const load = useCallback(() => {
    setRep(null);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(period.desde) || !/^\d{4}-\d{2}-\d{2}$/.test(period.hasta)) return undefined;
    let live = true;
    const t = setTimeout(() => {
      api("get", `/companies/${id}/gri306?desde=${period.desde}&hasta=${period.hasta}`).then((r) => live && setRep(r.data)).catch((e) => {
        if (!live) return;
        toast.error(errMsg(e));
        if (e?.response?.status !== 422) navigate(`/mis-empresas/${id}`);
      });
    }, 450);
    return () => { live = false; clearTimeout(t); };
  }, [api, id, period.desde, period.hasta, navigate]);

  const [tick, setTick] = useState(0);
  useEffect(() => load(), [load, tick]);

  return (
    <main className="max-w-6xl mx-auto px-5 lg:px-8 py-10 space-y-8" data-testid="gri-report-page">
      <Link to={`/mis-empresas/${id}`} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#05130e]" data-testid="gri-back-link"><ArrowLeft className="w-4 h-4" />Volver a la empresa</Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00b37a]">Reporte ESG · Residuos</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#05130e]">Informe GRI 306</h1>
          {rep && <p className="text-slate-600 mt-1" data-testid="gri-company">{rep.company.razon_social} · RUT {rep.company.rut} · ID RETC {rep.company.id_establecimiento_vu}</p>}
        </div>
        <Button disabled={!rep} onClick={() => downloadGriReport(rep)} className="rounded-full px-6 font-semibold" data-testid="gri-download-pdf"><FileDown className="w-4 h-4" />Exportar PDF</Button>
      </div>
      <PeriodPicker period={period} setPeriod={setPeriod} />
      {!rep ? <Skeleton className="h-96 rounded-3xl" /> : <Body id={id} rep={rep} reload={() => setTick((n) => n + 1)} />}
    </main>
  );
}

const Body = ({ id, rep, reload }) => (
  <>
    <KpiCards rep={rep} />
    <Narrative companyId={id} rep={rep} onChange={reload} />
    <Block code="306-3" title="Residuos generados" desc="Peso total generado en el establecimiento, separado por peligrosidad y por código LER (RETC / SINADER)." testid="gri-306-3">
      <GriTable testid="gri-306-3-hazard" cols={["Categoría", "Toneladas"]} rows={[["Residuos peligrosos (HAZMAT)", num(rep.gri306_3.peligrosos_t, 3)], ["Residuos no peligrosos", num(rep.gri306_3.no_peligrosos_t, 3)]]} />
      <GriTable testid="gri-306-3-ler" cols={["Código LER", "Descripción", "Familia", "Tipo", "Generado (t)", "Valorizado (t)"]}
        rows={rep.gri306_3.por_ler.map((r) => [r.codigo_ler, r.descripcion, r.familia, r.peligroso ? "Peligroso" : "No peligroso", num(r.toneladas, 3), num(r.valorizadas, 3)])} />
    </Block>
    <Block code="306-4" title="Residuos desviados de la eliminación" desc="Toneladas que ingresaron a valorización, por operación (según código LER) y lugar del tratamiento." testid="gri-306-4">
      <GriTable testid="gri-306-4-ops" cols={["Operación de valorización", "In-situ (t)", "Off-site (t)", "Total (t)"]}
        rows={rep.gri306_4.por_operacion.map((o) => [o.operacion, num(o.in_situ_t, 3), num(o.off_site_t, 3), num(o.total_t, 3)])} />
    </Block>
    <Block code="306-5" title="Residuos dirigidos a eliminación" desc="Todo lo transado en terraloop.cl se destina a valorización; los residuos enviados a relleno fuera de la plataforma no se registran aquí." testid="gri-306-5">
      <GriTable testid="gri-306-5-table" cols={["Destino", "Toneladas"]} rows={[["Relleno sanitario / vertedero", num(rep.gri306_5.relleno_t, 3)], ["Incineración sin recuperación de energía", num(rep.gri306_5.incineracion_t, 3)]]} />
    </Block>
    <Block code="Auditoría" title="Respaldo por retiro" desc={`Cada tonelada valorizada está respaldada por el ID RETC y la Resolución Sanitaria del gestor. ${num(rep.gestores_autorizados_pct, 0)}% recibido por gestores validados.`} testid="gri-evidence">
      <GriTable testid="gri-evidence-table" cols={["Certificado", "Fecha", "LER", "t", "Operación", "Gestor", "ID RETC", "Resolución · CVE", "Documento"]}
        rows={rep.evidencia.map((e) => [<Link key={e.codigo} to={`/verificar/${e.codigo}`} className="font-mono text-[#00915f] hover:underline inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />{e.codigo}</Link>, e.fecha, e.codigo_ler, num(e.toneladas, 3), e.operacion, `${e.gestor || "—"} · ${e.rut_gestor || "—"}`, e.retc_gestor, `${e.resolucion || "—"} · ${e.cve || "—"}`, e.documento])} />
    </Block>
  </>
);
